import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { SortDownArrow } from "../../assets/svg";
import Action from "../../components/common/Action";
import Breadcrumb from "../../components/common/Breadcrumb";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customtable/CustomTable";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import {
  getNotificationList,
  sendAgainNotification,
  deleteNotification,
} from "../../redux/notification/slice";

const Notification = () => {
  const [sortBy, setSortBy] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showSendAgainModal, setShowSendAgainModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // selector
  const { notificationList, notificationListLoading } = useSelector(
    (store) => ({
      notificationList:
        store?.notificationData?.getNotificationListStatus?.data?.data,
      notificationListLoading: store?.notificationData?.loading,
    }),
  );

  useEffect(() => {
    dispatch(
      getNotificationList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      }),
    );
  }, [dispatch, searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  const handleSendAgain = (item) => {
    setSelectedNotification(item);
    setShowSendAgainModal(true);
  };

  const handleConfirmSendAgain = () => {
    if (!selectedNotification) return;

    setIsLoading(true);
    const payload = {
      id: selectedNotification._id || selectedNotification.id,
      title: selectedNotification.title,
      body: selectedNotification.body,
    };

    if (selectedNotification.type && selectedNotification.type.trim() !== "") {
      payload.type = selectedNotification.type;
    }

    dispatch(sendAgainNotification(payload))
      .then((res) => {
        if (res.type === "sendAgainNotification/fulfilled") {
          dispatch(
            getNotificationList({
              search: searchValue,
              pageCount: pageCount,
              pageLimit: pageLimit,
              sort_by: sortBy,
              sort_direction: sortDirection === "asc" ? "asc" : "desc",
            }),
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowSendAgainModal(false);
        setSelectedNotification(null);
      });
  };

  const handleDelete = (item) => {
    setSelectedNotification(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedNotification) return;

    setIsLoading(true);
    const notificationId = selectedNotification._id || selectedNotification.id;

    dispatch(deleteNotification(notificationId))
      .then((res) => {
        if (res.type === "deleteNotification/fulfilled") {
          dispatch(
            getNotificationList({
              search: searchValue,
              pageCount: pageCount,
              pageLimit: pageLimit,
              sort_by: sortBy,
              sort_direction: sortDirection === "asc" ? "asc" : "desc",
            }),
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowDeleteModal(false);
        setSelectedNotification(null);
      });
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="w-24 table-th">No.</th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("title");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Title</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" ? "rotate-180" : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("body");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Body</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" ? "rotate-180" : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th">Type</th>
      <th className="table-th">Date & Time</th>
      <th className="table-th">Action</th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(notificationList?.data || []).map((item, index) => (
        <tr key={item.id} className="shadow-sm text-mgrey hover:bg-navTheme/5">
          <td className="px-5 py-3">
            {(pageCount - 1) * pageLimit + index + 1}
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-1 capitalize">{item?.title || "-"}</div>
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-1 capitalize">{item?.body || "-"}</div>
          </td>
          <td className="px-5 py-3">
            <div className="capitalize">{item?.type || "-"}</div>
          </td>
          <td className="px-5 py-3">
            {moment(item?.createdAt).format("DD-MM-YYYY hh:mm A")}
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => handleSendAgain(item)}
                  >
                    Send
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/add-notification", {
                        state: { notification: item, mode: "edit" },
                      })
                    }
                  >
                    Update
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => handleDelete(item)}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="space-y-5">
      <div>
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          Push Notification
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={"Push Notification"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
      </div>
      {/* Search and Export Section */}
      <div className="flex items-center space-x-4 w-full">
        <DebounceSearch
          className="full-width"
          search={searchValue}
          handleChange={setSearchValue}
          placeholder={"Search..."}
        />
        <button
          className="bg-themeBtn text-white flex items-center justify-center px-2 py-[9px] rounded uppercase font-figtree text-base w-60 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
          onClick={() => navigate("/add-notification")}
          type="button"
        >
          + Add Notification
        </button>
      </div>

      <div className="w-full bg-white shadow rounded-lg">
        <CustomTable
          overflowVisibleLg={true}
          columnHeaders={<ColumnHeaders />}
          dataRows={<DataRows />}
          data={notificationList}
          loading={notificationListLoading}
          showPagination={true}
          disableNext={pageCount >= notificationList?.pagination?.pages}
          disablePrev={pageCount <= 1}
          setPaginationCurrentLimit={setPageLimit}
          paginationCurrentLimit={pageLimit}
          current={pageCount}
          total={notificationList?.pagination?.total || 0}
          paginationData={(crPage) => setPageCount(crPage)}
          lastPage={notificationList?.pagination?.pages || 1}
        />
      </div>

      {showSendAgainModal && (
        <ConfirmationModel
          confirmText="Send Notification"
          onClose={() => {
            setShowSendAgainModal(false);
            setSelectedNotification(null);
          }}
          onConfirm={handleConfirmSendAgain}
          isLoading={isLoading}
        />
      )}

      {showDeleteModal && (
        <ConfirmationModel
          confirmText="Delete"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedNotification(null);
          }}
          onConfirm={handleConfirmDelete}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default Notification;
